import { isAndroidKiosk } from './androidBridge';

/**
 * The device's persisted local-mode identity (ADR-011): minted once with
 * `crypto.randomUUID()`, cached in `localStorage`, and - per posData's own
 * enrolment flow - pinned into the local SQLite database itself so a wiped
 * WebView storage can't orphan an already-provisioned device.
 *
 * Extracted out of posData/index.ts (Phase 6) because it's no longer only
 * that module's concern: lib/api.ts and lib/auth.tsx now send it too, so the
 * server can enforce terminal pinning (docs/android/PHASE-6-BASELINE-
 * AUDIT.md SS3) on login/refresh/switch-property, not only on sync calls.
 */
const DEVICE_ID_KEY = 'kortex.deviceId';

export function persistedDeviceId(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    window.localStorage.setItem(DEVICE_ID_KEY, minted);
    return minted;
  } catch {
    return crypto.randomUUID();
  }
}

/** INTERIM until Phase 4 server-issued codes (ADR-011): T + 2 chars of the id. */
export function interimDeviceCode(deviceId: string): string {
  const alnum = deviceId.replace(/[^a-z0-9]/gi, '').toUpperCase();
  return `T${alnum.slice(0, 2) || '01'}`;
}

/**
 * The device id to send with an auth call (login/refresh/switch-property),
 * or undefined outside the Android kiosk build. Ordinary browser tabs carry
 * no device-pinning concept at all - deliberately not `persistedDeviceId()`
 * unconditionally, which would mint and send an id for every plain web
 * sign-in for no reason.
 */
export function deviceIdForAuth(): string | undefined {
  return isAndroidKiosk() ? persistedDeviceId() : undefined;
}
